import { useState, useEffect, useCallback } from 'react';
import {
  Shield,
  Database,
  Cloud,
  AlertCircle,
  CheckCircle,
  Loader2,
  ArrowLeft,
  ArrowRight,
  Plug,
  Server,
  Lock,
  Eye,
  EyeOff,
  RefreshCw,
  Settings,
  Zap,
  Monitor,
  MessageSquare,
  Bell,
  Search,
  UserCheck,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ConnectorStatus = 'disconnected' | 'connected' | 'error';
type AuthType = 'apiKey' | 'oauth' | 'basic' | 'token' | 'apiKeyUrl';
type WizardStep = 'credentials' | 'test' | 'options' | 'done';
type CategoryId =
  | 'siem'
  | 'edr'
  | 'iam'
  | 'cloud'
  | 'ticketing'
  | 'threat_intel'
  | 'communication'
  | 'incident';

interface CredentialField {
  key: string;
  label: string;
  type: 'text' | 'password';
  placeholder: string;
}

interface ConnectorDef {
  id: string;
  name: string;
  category: CategoryId;
  authType: AuthType;
  fields: CredentialField[];
}

interface ConnectorState {
  status: ConnectorStatus;
  credentials: Record<string, string>;
  pollInterval: number;
  enabled: boolean;
  lastTested?: string;
}

interface CategoryDef {
  id: CategoryId;
  label: string;
  icon: React.ReactNode;
  description: string;
}

// ---------------------------------------------------------------------------
// Storage key
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'zoroagent_connectors';

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

const CATEGORIES: CategoryDef[] = [
  {
    id: 'siem',
    label: 'SIEM',
    icon: <Database className="w-6 h-6" />,
    description: 'Security Information & Event Management',
  },
  {
    id: 'edr',
    label: 'EDR / XDR',
    icon: <Monitor className="w-6 h-6" />,
    description: 'Endpoint Detection & Response',
  },
  {
    id: 'iam',
    label: 'IAM',
    icon: <UserCheck className="w-6 h-6" />,
    description: 'Identity & Access Management',
  },
  {
    id: 'cloud',
    label: 'Cloud',
    icon: <Cloud className="w-6 h-6" />,
    description: 'Cloud Security Posture',
  },
  {
    id: 'ticketing',
    label: 'Ticketing',
    icon: <Server className="w-6 h-6" />,
    description: 'Issue Tracking & ITSM',
  },
  {
    id: 'threat_intel',
    label: 'Threat Intel',
    icon: <Search className="w-6 h-6" />,
    description: 'Threat Intelligence Feeds',
  },
  {
    id: 'communication',
    label: 'Communication',
    icon: <MessageSquare className="w-6 h-6" />,
    description: 'Alerting & Notifications',
  },
  {
    id: 'incident',
    label: 'Incident',
    icon: <Bell className="w-6 h-6" />,
    description: 'Incident Response & On-Call',
  },
];

// ---------------------------------------------------------------------------
// Credential field presets
// ---------------------------------------------------------------------------

const apiKeyField: CredentialField = {
  key: 'apiKey',
  label: 'API Key',
  type: 'password',
  placeholder: 'Enter API key',
};
const baseUrlField: CredentialField = {
  key: 'baseUrl',
  label: 'Base URL',
  type: 'text',
  placeholder: 'https://your-instance.example.com',
};
const tenantIdField: CredentialField = {
  key: 'tenantId',
  label: 'Tenant ID',
  type: 'text',
  placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
};
const clientIdField: CredentialField = {
  key: 'clientId',
  label: 'Client ID',
  type: 'text',
  placeholder: 'Application (client) ID',
};
const clientSecretField: CredentialField = {
  key: 'clientSecret',
  label: 'Client Secret',
  type: 'password',
  placeholder: 'Client secret value',
};

// ---------------------------------------------------------------------------
// Connector catalog
// ---------------------------------------------------------------------------

const CONNECTORS: ConnectorDef[] = [
  // SIEM
  {
    id: 'splunk',
    name: 'Splunk',
    category: 'siem',
    authType: 'apiKey',
    fields: [baseUrlField, apiKeyField],
  },
  {
    id: 'sentinel',
    name: 'Microsoft Sentinel',
    category: 'siem',
    authType: 'oauth',
    fields: [tenantIdField, clientIdField, clientSecretField],
  },
  {
    id: 'elastic',
    name: 'Elastic SIEM',
    category: 'siem',
    authType: 'apiKeyUrl',
    fields: [baseUrlField, apiKeyField],
  },

  // EDR / XDR
  {
    id: 'crowdstrike',
    name: 'CrowdStrike',
    category: 'edr',
    authType: 'apiKey',
    fields: [clientIdField, { ...clientSecretField, key: 'apiKey', label: 'API Key' }],
  },
  {
    id: 'defender',
    name: 'Microsoft Defender',
    category: 'edr',
    authType: 'oauth',
    fields: [tenantIdField, clientIdField, clientSecretField],
  },
  {
    id: 'sentinelone',
    name: 'SentinelOne',
    category: 'edr',
    authType: 'apiKey',
    fields: [baseUrlField, apiKeyField],
  },

  // IAM
  {
    id: 'okta',
    name: 'Okta',
    category: 'iam',
    authType: 'token',
    fields: [
      { key: 'domain', label: 'Okta Domain', type: 'text', placeholder: 'your-org.okta.com' },
      { key: 'apiToken', label: 'API Token', type: 'password', placeholder: 'SSWS token' },
    ],
  },
  {
    id: 'entra',
    name: 'Microsoft Entra ID',
    category: 'iam',
    authType: 'oauth',
    fields: [tenantIdField, clientIdField, clientSecretField],
  },

  // Cloud
  {
    id: 'aws_security',
    name: 'AWS Security Hub',
    category: 'cloud',
    authType: 'apiKey',
    fields: [
      { key: 'accessKeyId', label: 'Access Key ID', type: 'text', placeholder: 'AKIA...' },
      {
        key: 'secretAccessKey',
        label: 'Secret Access Key',
        type: 'password',
        placeholder: 'Secret key',
      },
      { key: 'region', label: 'Region', type: 'text', placeholder: 'us-east-1' },
    ],
  },
  {
    id: 'azure_security',
    name: 'Azure Security',
    category: 'cloud',
    authType: 'oauth',
    fields: [tenantIdField, clientIdField, clientSecretField],
  },
  {
    id: 'gcp_scc',
    name: 'Google Cloud SCC',
    category: 'cloud',
    authType: 'apiKey',
    fields: [
      { key: 'projectId', label: 'Project ID', type: 'text', placeholder: 'my-gcp-project' },
      {
        key: 'apiKey',
        label: 'Service Account Key (JSON)',
        type: 'password',
        placeholder: 'Paste JSON key or path',
      },
    ],
  },

  // Ticketing
  {
    id: 'jira',
    name: 'Jira',
    category: 'ticketing',
    authType: 'apiKeyUrl',
    fields: [
      {
        key: 'baseUrl',
        label: 'Jira URL',
        type: 'text',
        placeholder: 'https://your-org.atlassian.net',
      },
      { key: 'email', label: 'Email', type: 'text', placeholder: 'user@company.com' },
      { key: 'apiToken', label: 'API Token', type: 'password', placeholder: 'Atlassian API token' },
    ],
  },
  {
    id: 'servicenow',
    name: 'ServiceNow',
    category: 'ticketing',
    authType: 'basic',
    fields: [
      {
        key: 'baseUrl',
        label: 'Instance URL',
        type: 'text',
        placeholder: 'https://your-instance.service-now.com',
      },
      { key: 'username', label: 'Username', type: 'text', placeholder: 'admin' },
      { key: 'password', label: 'Password', type: 'password', placeholder: 'Password' },
    ],
  },

  // Threat Intel
  {
    id: 'virustotal',
    name: 'VirusTotal',
    category: 'threat_intel',
    authType: 'apiKey',
    fields: [apiKeyField],
  },
  {
    id: 'abuseipdb',
    name: 'AbuseIPDB',
    category: 'threat_intel',
    authType: 'apiKey',
    fields: [apiKeyField],
  },
  {
    id: 'alienvault',
    name: 'AlienVault OTX',
    category: 'threat_intel',
    authType: 'apiKey',
    fields: [apiKeyField],
  },

  // Communication
  {
    id: 'slack',
    name: 'Slack',
    category: 'communication',
    authType: 'apiKey',
    fields: [
      { key: 'apiKey', label: 'Bot Token', type: 'password', placeholder: 'xoxb-...' },
      { key: 'channel', label: 'Default Channel', type: 'text', placeholder: '#security-alerts' },
    ],
  },
  {
    id: 'teams',
    name: 'Microsoft Teams',
    category: 'communication',
    authType: 'oauth',
    fields: [tenantIdField, clientIdField, clientSecretField],
  },

  // Incident
  {
    id: 'pagerduty',
    name: 'PagerDuty',
    category: 'incident',
    authType: 'apiKey',
    fields: [apiKeyField],
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function loadState(): Record<string, ConnectorState> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore corrupt data */
  }
  return {};
}

function saveState(state: Record<string, ConnectorState>): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

function getConnectorsByCategory(cat: CategoryId): ConnectorDef[] {
  return CONNECTORS.filter(c => c.category === cat);
}

function statusBadge(status: ConnectorStatus): React.ReactNode {
  switch (status) {
    case 'connected':
      return (
        <span className="badge badge-success flex items-center gap-1">
          <CheckCircle className="w-3 h-3" /> Connected
        </span>
      );
    case 'error':
      return (
        <span className="badge badge-danger flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> Error
        </span>
      );
    default:
      return (
        <span className="badge badge-warning flex items-center gap-1">
          <Plug className="w-3 h-3" /> Not Connected
        </span>
      );
  }
}

const WIZARD_STEPS: { key: WizardStep; label: string }[] = [
  { key: 'credentials', label: 'Credentials' },
  { key: 'test', label: 'Test' },
  { key: 'options', label: 'Options' },
  { key: 'done', label: 'Done' },
];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Password field with show/hide toggle */
function SecretInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <input
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="input pr-10"
      />
      <button
        type="button"
        onClick={() => setVisible(v => !v)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 transition-colors"
      >
        {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ConnectorSetup() {
  // Persisted connector state (keyed by connector id)
  const [connectorStates, setConnectorStates] = useState<Record<string, ConnectorState>>(loadState);

  // Navigation state
  const [selectedCategory, setSelectedCategory] = useState<CategoryId | null>(null);
  const [selectedConnector, setSelectedConnector] = useState<ConnectorDef | null>(null);
  const [wizardStep, setWizardStep] = useState<WizardStep>('credentials');

  // Form state while in wizard
  const [formCreds, setFormCreds] = useState<Record<string, string>>({});
  const [formPollInterval, setFormPollInterval] = useState(60);
  const [formEnabled, setFormEnabled] = useState(true);

  // Test connection state
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<'success' | 'failure' | null>(null);

  // Persist on change
  useEffect(() => {
    saveState(connectorStates);
  }, [connectorStates]);

  // ------ Derived data ------
  const totalConnectors = CONNECTORS.length;
  const connectedCount = Object.values(connectorStates).filter(
    s => s.status === 'connected',
  ).length;

  const getState = useCallback(
    (id: string): ConnectorState => {
      return (
        connectorStates[id] ?? {
          status: 'disconnected',
          credentials: {},
          pollInterval: 60,
          enabled: false,
        }
      );
    },
    [connectorStates],
  );

  // ------ Handlers ------

  const openConnectorWizard = (connector: ConnectorDef) => {
    const existing = getState(connector.id);
    setSelectedConnector(connector);
    setFormCreds(existing.credentials);
    setFormPollInterval(existing.pollInterval);
    setFormEnabled(existing.enabled);
    setWizardStep('credentials');
    setTestResult(null);
    setTesting(false);
  };

  const goBack = () => {
    if (selectedConnector) {
      if (wizardStep === 'credentials') {
        setSelectedConnector(null);
      } else {
        const idx = WIZARD_STEPS.findIndex(s => s.key === wizardStep);
        if (idx > 0) setWizardStep(WIZARD_STEPS[idx - 1].key);
      }
    } else if (selectedCategory) {
      setSelectedCategory(null);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    // Simulate a connection test (1.5 - 2.5s)
    await new Promise(r => setTimeout(r, 1500 + Math.random() * 1000));
    // Check that all required fields are filled
    const allFilled = selectedConnector?.fields.every(f => formCreds[f.key]?.trim());
    setTestResult(allFilled ? 'success' : 'failure');
    setTesting(false);
  };

  const handleSave = () => {
    if (!selectedConnector) return;
    setConnectorStates(prev => ({
      ...prev,
      [selectedConnector.id]: {
        status: testResult === 'success' ? 'connected' : 'error',
        credentials: formCreds,
        pollInterval: formPollInterval,
        enabled: formEnabled,
        lastTested: new Date().toISOString(),
      },
    }));
    setWizardStep('done');
  };

  const handleDisconnect = (id: string) => {
    setConnectorStates(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleDone = () => {
    setSelectedConnector(null);
    setSelectedCategory(null);
  };

  // ------ Render helpers ------

  const renderHealthBar = () => {
    const pct = totalConnectors > 0 ? (connectedCount / totalConnectors) * 100 : 0;
    return (
      <div className="glass rounded-2xl p-6 mb-6 animate-fade-in">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-lg shadow-primary-500/25">
              <Plug className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Connector Health</h2>
              <p className="text-sm text-slate-400">
                {connectedCount} of {totalConnectors} connectors active
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary-400" />
            <span className="text-2xl font-bold text-white">{Math.round(pct)}%</span>
          </div>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  };

  // ---- Category grid view ----
  const renderCategoryGrid = () => (
    <div className="animate-slide-up">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {CATEGORIES.map(cat => {
          const catConnectors = getConnectorsByCategory(cat.id);
          const catConnected = catConnectors.filter(
            c => getState(c.id).status === 'connected',
          ).length;
          return (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className="glass rounded-xl p-5 text-left hover:bg-slate-700/50 hover:border-slate-600/50 transition-all duration-200 group"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 rounded-lg bg-primary-500/10 text-primary-400 group-hover:bg-primary-500/20 transition-colors">
                  {cat.icon}
                </div>
                {catConnected > 0 && (
                  <span className="badge badge-success text-[10px]">
                    {catConnected}/{catConnectors.length}
                  </span>
                )}
              </div>
              <h3 className="font-semibold text-white mb-1">{cat.label}</h3>
              <p className="text-xs text-slate-400 leading-relaxed">{cat.description}</p>
              <p className="text-xs text-slate-500 mt-2">
                {catConnectors.length} connector{catConnectors.length !== 1 ? 's' : ''}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );

  // ---- Connector list within a category ----
  const renderConnectorList = () => {
    if (!selectedCategory) return null;
    const cat = CATEGORIES.find(c => c.id === selectedCategory)!;
    const catConnectors = getConnectorsByCategory(selectedCategory);

    return (
      <div className="animate-slide-up">
        <button
          onClick={() => setSelectedCategory(null)}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back to categories
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-primary-500/10 text-primary-400">{cat.icon}</div>
          <div>
            <h3 className="text-lg font-bold text-white">{cat.label}</h3>
            <p className="text-sm text-slate-400">{cat.description}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {catConnectors.map(connector => {
            const state = getState(connector.id);
            return (
              <div
                key={connector.id}
                className="glass rounded-xl p-5 hover:bg-slate-700/50 hover:border-slate-600/50 transition-all duration-200"
              >
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-white">{connector.name}</h4>
                  {statusBadge(state.status)}
                </div>
                <p className="text-xs text-slate-500 mb-4">
                  Auth:{' '}
                  {connector.authType === 'oauth'
                    ? 'OAuth / Client Credentials'
                    : connector.authType === 'basic'
                      ? 'Username & Password'
                      : connector.authType === 'token'
                        ? 'Domain + Token'
                        : connector.authType === 'apiKeyUrl'
                          ? 'API Key + URL'
                          : 'API Key'}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openConnectorWizard(connector)}
                    className="btn btn-primary text-sm flex-1 flex items-center justify-center gap-1.5"
                  >
                    <Settings className="w-3.5 h-3.5" />
                    {state.status === 'connected' ? 'Reconfigure' : 'Configure'}
                  </button>
                  {state.status === 'connected' && (
                    <button
                      onClick={() => handleDisconnect(connector.id)}
                      className="btn btn-ghost text-sm text-danger-400 hover:text-danger-300 px-3"
                      title="Disconnect"
                    >
                      <Plug className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ---- Wizard step: credentials ----
  const renderCredentials = () => {
    if (!selectedConnector) return null;
    return (
      <div className="space-y-4 animate-slide-up">
        <p className="text-sm text-slate-400 mb-2">
          Enter credentials for{' '}
          <span className="text-white font-medium">{selectedConnector.name}</span>. These are stored
          locally in your browser.
        </p>
        {selectedConnector.fields.map(field => (
          <div key={field.key}>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">{field.label}</label>
            {field.type === 'password' ? (
              <SecretInput
                value={formCreds[field.key] ?? ''}
                onChange={v => setFormCreds(prev => ({ ...prev, [field.key]: v }))}
                placeholder={field.placeholder}
              />
            ) : (
              <input
                type="text"
                value={formCreds[field.key] ?? ''}
                onChange={e => setFormCreds(prev => ({ ...prev, [field.key]: e.target.value }))}
                placeholder={field.placeholder}
                className="input"
              />
            )}
          </div>
        ))}
        <div className="flex justify-end pt-2">
          <button
            onClick={() => setWizardStep('test')}
            className="btn btn-primary flex items-center gap-2"
            disabled={!selectedConnector.fields.every(f => formCreds[f.key]?.trim())}
          >
            Next <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  // ---- Wizard step: test ----
  const renderTest = () => (
    <div className="flex flex-col items-center justify-center py-8 animate-slide-up">
      {testing ? (
        <>
          <Loader2 className="w-16 h-16 text-primary-400 animate-spin mb-4" />
          <p className="text-lg text-white font-medium mb-1">Testing connection...</p>
          <p className="text-sm text-slate-400">
            Verifying credentials with {selectedConnector?.name}
          </p>
        </>
      ) : testResult === 'success' ? (
        <>
          <div className="relative mb-4">
            <div className="absolute inset-0 bg-success-500/20 blur-2xl rounded-full" />
            <CheckCircle className="w-16 h-16 text-success-400 relative animate-scale-in" />
          </div>
          <p className="text-lg text-white font-medium mb-1">Connection successful</p>
          <p className="text-sm text-slate-400">
            {selectedConnector?.name} is reachable and authenticated.
          </p>
        </>
      ) : testResult === 'failure' ? (
        <>
          <div className="relative mb-4">
            <div className="absolute inset-0 bg-danger-500/20 blur-2xl rounded-full" />
            <AlertCircle className="w-16 h-16 text-danger-400 relative animate-scale-in" />
          </div>
          <p className="text-lg text-white font-medium mb-1">Connection failed</p>
          <p className="text-sm text-slate-400 mb-4">Check your credentials and try again.</p>
          <button
            onClick={() => setWizardStep('credentials')}
            className="btn btn-ghost text-sm flex items-center gap-1.5"
          >
            <ArrowLeft className="w-4 h-4" /> Edit credentials
          </button>
        </>
      ) : (
        <>
          <Shield className="w-16 h-16 text-slate-600 mb-4" />
          <p className="text-lg text-white font-medium mb-1">Ready to test</p>
          <p className="text-sm text-slate-400 mb-6">
            We will verify your credentials against the {selectedConnector?.name} API.
          </p>
          <button
            onClick={handleTestConnection}
            className="btn btn-primary flex items-center gap-2"
          >
            <Zap className="w-4 h-4" /> Test Connection
          </button>
        </>
      )}

      {!testing && testResult && (
        <div className="flex gap-3 mt-6">
          {testResult === 'success' && (
            <button
              onClick={() => setWizardStep('options')}
              className="btn btn-primary flex items-center gap-2"
            >
              Next <ArrowRight className="w-4 h-4" />
            </button>
          )}
          {testResult === 'failure' && (
            <button
              onClick={handleTestConnection}
              className="btn btn-primary flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" /> Retry
            </button>
          )}
        </div>
      )}
    </div>
  );

  // ---- Wizard step: options ----
  const renderOptions = () => (
    <div className="space-y-6 animate-slide-up">
      <p className="text-sm text-slate-400">
        Configure polling and behaviour for{' '}
        <span className="text-white font-medium">{selectedConnector?.name}</span>.
      </p>

      <div>
        <label className="block text-sm font-medium text-slate-300 mb-1.5">
          Polling Interval (seconds)
        </label>
        <div className="flex items-center gap-4">
          <input
            type="range"
            min={10}
            max={300}
            step={10}
            value={formPollInterval}
            onChange={e => setFormPollInterval(Number(e.target.value))}
            className="flex-1 accent-primary-500"
          />
          <span className="text-white font-mono text-sm w-16 text-right">{formPollInterval}s</span>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          How often ZoroAgent polls this connector for new data.
        </p>
      </div>

      <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
        <div>
          <p className="text-sm font-medium text-white">Enable connector</p>
          <p className="text-xs text-slate-400">Start polling immediately after saving.</p>
        </div>
        <button
          onClick={() => setFormEnabled(v => !v)}
          className={`w-12 h-6 rounded-full transition-colors duration-200 relative ${formEnabled ? 'bg-primary-500' : 'bg-slate-600'}`}
        >
          <span
            className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${formEnabled ? 'translate-x-6' : 'translate-x-0.5'}`}
          />
        </button>
      </div>

      <div className="flex justify-between pt-2">
        <button
          onClick={() => setWizardStep('test')}
          className="btn btn-ghost flex items-center gap-2 text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button onClick={handleSave} className="btn btn-success flex items-center gap-2">
          <CheckCircle className="w-4 h-4" /> Save & Finish
        </button>
      </div>
    </div>
  );

  // ---- Wizard step: done ----
  const renderDone = () => (
    <div className="flex flex-col items-center justify-center py-10 animate-slide-up">
      <div className="relative mb-6">
        <div className="absolute inset-0 bg-success-500/20 blur-3xl rounded-full animate-pulse-slow" />
        <CheckCircle className="w-20 h-20 text-success-400 relative animate-scale-in" />
      </div>
      <h3 className="text-2xl font-bold text-white mb-2">{selectedConnector?.name} configured</h3>
      <p className="text-sm text-slate-400 mb-8">
        The connector is {formEnabled ? 'enabled and polling' : 'saved but disabled'}. You can
        reconfigure it anytime.
      </p>
      <button onClick={handleDone} className="btn btn-primary px-8">
        Done
      </button>
    </div>
  );

  // ---- Wizard wrapper ----
  const renderWizard = () => {
    if (!selectedConnector) return null;
    const currentIdx = WIZARD_STEPS.findIndex(s => s.key === wizardStep);

    return (
      <div className="animate-slide-up">
        {/* Back + connector name */}
        <button
          onClick={goBack}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />{' '}
          {wizardStep === 'credentials' ? 'Back to connectors' : 'Back'}
        </button>

        <div className="glass rounded-2xl p-6">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-primary-500/10 text-primary-400">
              <Lock className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">{selectedConnector.name}</h3>
              <p className="text-xs text-slate-400">Connector configuration wizard</p>
            </div>
          </div>

          {/* Step indicators */}
          <div className="flex items-center gap-2 mb-8">
            {WIZARD_STEPS.map((s, i) => (
              <div key={s.key} className="flex items-center gap-2 flex-1">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                    i < currentIdx
                      ? 'bg-success-500/20 text-success-400 border border-success-500/30'
                      : i === currentIdx
                        ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/25'
                        : 'bg-slate-800 text-slate-500 border border-slate-700'
                  }`}
                >
                  {i < currentIdx ? <CheckCircle className="w-4 h-4" /> : i + 1}
                </div>
                <span
                  className={`text-xs hidden sm:inline ${i === currentIdx ? 'text-white font-medium' : 'text-slate-500'}`}
                >
                  {s.label}
                </span>
                {i < WIZARD_STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-px ${i < currentIdx ? 'bg-success-500/40' : 'bg-slate-700'}`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Step content */}
          {wizardStep === 'credentials' && renderCredentials()}
          {wizardStep === 'test' && renderTest()}
          {wizardStep === 'options' && renderOptions()}
          {wizardStep === 'done' && renderDone()}
        </div>
      </div>
    );
  };

  // ------ Main render ------
  return (
    <div className="space-y-6">
      {renderHealthBar()}

      {selectedConnector
        ? renderWizard()
        : selectedCategory
          ? renderConnectorList()
          : renderCategoryGrid()}
    </div>
  );
}
